import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Bell, Package, Truck, Check, X, Settings, MessageSquare, CircleAlert, CheckCircle, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

type Notification = {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  loadId?: string;
  truckId?: string;
  relatedUserId?: string;
  matchScore?: number;
  read: boolean;
  dismissed: boolean;
  createdAt: string;
};

export function NotificationCenter() {
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);

  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ["/api/notifications/unread-count"],
    refetchInterval: 30000,
  });

  const { data: notifications, isLoading } = useQuery<Notification[]>({
    queryKey: ["/api/notifications"],
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/notifications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const unreadCount = countData?.count ?? 0;
  const activeNotifications = (notifications || []).filter(n => !n.dismissed);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification.id);
    }
    setOpen(false);
    
    if (notification.type === 'availability_check') {
      setLocation('/dashboard');
    } else if (notification.type === 'message' && notification.relatedUserId) {
      const loadParam = notification.loadId ? `&load=${notification.loadId}` : '';
      setLocation(`/shipments?user=${notification.relatedUserId}${loadParam}`);
    } else if (notification.type === 'load_confirmed') {
      if (notification.relatedUserId && notification.loadId) {
        setLocation(`/shipments?user=${notification.relatedUserId}&load=${notification.loadId}`);
      } else if (notification.loadId) {
        setLocation(`/shipments`);
      }
    } else if (notification.type === 'quote_declined') {
      if (notification.relatedUserId && notification.loadId) {
        setLocation(`/shipments?user=${notification.relatedUserId}&load=${notification.loadId}`);
      } else {
        setLocation(`/shipments`);
      }
    } else if (notification.type === 'load_posted') {
      if (notification.loadId) {
        setLocation(`/find-loads?highlight=${notification.loadId}`);
      }
    } else if (notification.type === 'match_truck') {
      // Shipper matched with a carrier — show filtered carrier search for this load
      if (notification.loadId) {
        setLocation(`/find-trucks?load=${notification.loadId}`);
      }
    } else {
      // Generic data-driven fallback — works for any type not explicitly handled above
      // (match_load, system_notification, referral_signup, and all current/future notification types)
      if (notification.relatedUserId && notification.loadId) {
        setLocation(`/shipments?user=${notification.relatedUserId}&load=${notification.loadId}`);
      } else if (notification.loadId) {
        setLocation(`/shipments?load=${notification.loadId}`);
      } else if (notification.truckId) {
        setLocation(`/find-loads?truck=${notification.truckId}`);
      }
      // else: no actionable destination — close panel only (already done above)
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "load_confirmed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "quote_declined":
        return <XCircle className="h-4 w-4 text-muted-foreground" />;
      case "load_posted":
        return <Truck className="h-4 w-4 text-blue-600" />;
      case "match_load":
        return <Package className="h-4 w-4 text-foreground" />;
      case "match_truck":
        return <Truck className="h-4 w-4 text-foreground" />;
      case "message":
        return <MessageSquare className="h-4 w-4 text-primary" />;
      case "availability_check":
        return <CircleAlert className="h-4 w-4 text-amber-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1"
              variant="destructive"
              data-testid="badge-notification-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold" data-testid="text-notifications-title">Notifications</h4>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                data-testid="button-mark-all-read"
              >
                <Check className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setOpen(false);
                setLocation("/preferences");
              }}
              data-testid="button-notification-settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <ScrollArea className="h-80">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-full"></div>
                </div>
              ))}
            </div>
          ) : activeNotifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p data-testid="text-no-notifications">No notifications yet</p>
              <p className="text-sm mt-1">
                Set up your preferences to get match alerts
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setOpen(false);
                  setLocation("/preferences");
                }}
                data-testid="button-setup-preferences"
              >
                Set up preferences
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {activeNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 hover-elevate cursor-pointer ${
                    !notification.read ? "bg-primary/5" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm truncate">
                          {notification.title}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissMutation.mutate(notification.id);
                          }}
                          data-testid={`button-dismiss-${notification.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                          })}
                        </span>
                        {notification.matchScore && (
                          <Badge variant="secondary" className="text-xs">
                            {notification.matchScore}% match
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
